import { PowerDevice, PowerHistorySample } from 'src/api/power';
import { MetricSeries } from '@/shared/metric-chart/metric-chart.component';

/**
 * Per-outlet average watts, one series per metered outlet in channel order (so each outlet keeps
 * its colour). Falls back to the total when no outlet statistics exist.
 */
export function outletWattSeries(device: PowerDevice, samples: PowerHistorySample[], totalLabel: string): MetricSeries[] {
    const outletIds = [...new Set(samples.flatMap((s) => s.outlets.map((o) => o.outletId)))].sort((a, b) => a - b);
    if (outletIds.length === 0) return totalWattSeries(device, samples, totalLabel);

    const names = new Map(device.outlets.map((o) => [o.id, o.name]));
    return outletIds.map((id) => ({
        key: `outlet-${id}`,
        label: names.get(id) ?? samples.flatMap((s) => s.outlets).find((o) => o.outletId === id)?.name ?? `Outlet ${id}`,
        points: samples.flatMap((s) => {
            const o = s.outlets.find((x) => x.outletId === id);
            return o ? [{ t: Date.parse(s.timestampUtc), v: o.avgWatts }] : [];
        })
    }));
}

/** Total average watts as a single series. */
export function totalWattSeries(device: PowerDevice, samples: PowerHistorySample[], label: string): MetricSeries[] {
    const points = samples.filter((s) => s.totalAvgWatts != null).map((s) => ({ t: Date.parse(s.timestampUtc), v: s.totalAvgWatts as number }));
    return points.length ? [{ key: `total-${device.key}`, label, points }] : [];
}

/** Totals over a history window, computed the same way for every transport. */
export interface PowerHistorySummary {
    /** Energy over the window in kWh, from each period's average watts and length. */
    energyKWh: number | null;
    /** Highest combined average over any period. */
    peakWatts: number | null;
    /** Mean of the combined averages. */
    avgWatts: number | null;
}

/**
 * One chart for every PDU: a series per device, stacked, so the top of the stack is the room's total.
 * Samples are aligned to the period boundary so devices that started at different times stack
 * correctly; a device with no sample in a period counts as 0 W there (the chart stacks by index).
 * With a single device, its per-outlet series are used instead, as before.
 */
export function combinedWattSeries(devices: PowerDevice[], histories: Record<string, PowerHistorySample[]>, totalLabel: string): MetricSeries[] {
    const withData = devices.filter((d) => (histories[d.key] ?? []).length > 0);
    if (withData.length === 0) return [];
    if (withData.length === 1) return outletWattSeries(withData[0], histories[withData[0].key], totalLabel);

    const buckets = alignedBuckets(withData.map((d) => histories[d.key]));
    return withData.map((device) => {
        const byBucket = new Map<number, number>();
        for (const s of histories[device.key]) {
            if (s.totalAvgWatts == null) continue;
            const t = bucketOf(s);
            byBucket.set(t, (byBucket.get(t) ?? 0) + s.totalAvgWatts);
        }
        return {
            key: `device-${device.key}`,
            label: device.name,
            points: buckets.map((t) => ({ t, v: byBucket.get(t) ?? 0 }))
        };
    });
}

/** Energy, peak and average across every device's history. */
export function powerSummary(histories: PowerHistorySample[][]): PowerHistorySummary {
    const combined = new Map<number, number>();
    let energyKWh = 0;
    let hasEnergy = false;
    for (const samples of histories) {
        for (const s of samples) {
            if (s.totalAvgWatts == null) continue;
            const t = bucketOf(s);
            combined.set(t, (combined.get(t) ?? 0) + s.totalAvgWatts);
            energyKWh += (s.totalAvgWatts * (s.periodSeconds || 300)) / 3600 / 1000;
            hasEnergy = true;
        }
    }
    if (combined.size === 0) return { energyKWh: null, peakWatts: null, avgWatts: null };
    const values = [...combined.values()];
    return {
        energyKWh: hasEnergy ? energyKWh : null,
        peakWatts: Math.max(...values),
        avgWatts: values.reduce((a, b) => a + b, 0) / values.length
    };
}

/** Sum of the devices' current total readings; null when none reports one. */
export function currentWatts(devices: PowerDevice[]): number | null {
    const totals = devices.map((d) => d.total?.watts).filter((w): w is number => w != null);
    return totals.length ? totals.reduce((a, b) => a + b, 0) : null;
}

/** Start of the sample's period, so samples from different devices land in the same bucket. */
function bucketOf(sample: PowerHistorySample): number {
    const periodMs = (sample.periodSeconds || 300) * 1000;
    return Math.floor(Date.parse(sample.timestampUtc) / periodMs) * periodMs;
}

function alignedBuckets(histories: PowerHistorySample[][]): number[] {
    const set = new Set<number>();
    for (const samples of histories) for (const s of samples) if (s.totalAvgWatts != null) set.add(bucketOf(s));
    return [...set].sort((a, b) => a - b);
}
